import { useState } from "react";
import { Creative, VideoRetentionData, CreativeReportColumn } from "../../types";
import CreativeGalleryPreview from "../preview/CreativeGalleryPreview";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { capitalizeFirstLetter, getMediaIcon } from "@/screens/utils/Helpers";
import { ScrollableFadeContainer } from "../table/CreativeTable";
import { Button } from "@/components/ui/button";
import {
  RiArrowLeftWideLine,
  RiArrowRightWideLine,
  RiPantoneLine,
  RiSideBarLine,
} from "@remixicon/react";
import { LuLoaderCircle } from "react-icons/lu";

interface CardHeaderProps {
  title: string;
  currentGroup: string;
  currentAdIndex?: number;
  totalAds?: number;
  onPanelClick?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  mediaType: "image" | "video" | "mixed";
  mediaItems?: (Creative & {
    type: "image" | "video" | "mixed";
    ad_name: string;
    ad_id?: string;
    creative_id?: string;
    videoRetentionData?: VideoRetentionData;
  } & Record<string, unknown>)[];
  groupType?: string;
  creativeType?: string;
  labelView?: string;
  labelConfigId?: string;
  accountId?: string;
  onGalleryOpen?: () => void;
  onGalleryClose?: () => void;
  uniqueLabels?: string[];
  sequenceData?: Array<{ label: string; order: number }>;
  ad_columns?: CreativeReportColumn[];
  isAdsLoading?: boolean;
  galleryTotalCreatives?: number;
  onGalleryLoadMore?: () => Promise<void>;
  galleryOpen?: boolean;
  setGalleryOpen?: (open: boolean) => void;
  isGalleryLoading?: boolean;
}

const CardHeader = ({
  title,
  currentGroup,
  currentAdIndex,
  totalAds,
  mediaType,
  onPrevious,
  onNext,
  onPanelClick,
  mediaItems = [],
  groupType,
  creativeType,
  labelView,
  labelConfigId,
  accountId,
  onGalleryOpen,
  onGalleryClose,
  uniqueLabels = [],
  sequenceData,
  ad_columns,
  isAdsLoading = false,
  galleryTotalCreatives,
  onGalleryLoadMore,
  galleryOpen,
  setGalleryOpen,
  isGalleryLoading,
}: CardHeaderProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isGalleryOpen = galleryOpen !== undefined ? galleryOpen : internalOpen;

  const handleGalleryViewChange = (open: boolean) => {
    if (open) {
      if (galleryOpen !== undefined) {
        onGalleryOpen?.();
      } else {
        setInternalOpen(true);
        onGalleryOpen?.();
      }
    } else {
      if (setGalleryOpen) {
        setGalleryOpen(false);
      } else {
        setInternalOpen(false);
      }
      onGalleryClose?.();
    }
  };

  return (
    <TooltipProvider>
      <div className="w-full flex flex-col">
        <div className="flex items-center justify-between gap-4">
          {labelView === "sequence" && sequenceData && sequenceData.length > 0 ? (
            <div className="w-full">
              <ScrollableFadeContainer
                sequenceData={sequenceData}
                uniqueLabels={uniqueLabels}
                disableHoverGradient={true}
              />
            </div>
          ) : (
            <Tooltip>
              <TooltipTrigger className="w-full">
                <h3 className="px-1 text-left overflow-hidden text-brand-primary-800 text-ellipsis whitespace-nowrap text-[12px] font-medium leading-[20px]">
                  {title}
                </h3>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" className="max-w-[400px]">
                <p className="text-[12px] text-white">{title}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex items-center gap-3 py-2 overflow-x-auto">
          {totalAds !== undefined && totalAds > 0 && (
            <div className="w-full flex items-center justify-between gap-1">
              <div className="flex items-center justify-center gap-1">
                <Tooltip>
                  <TooltipTrigger>
                    <div className="shrink-0 rounded-[8px] flex items-center justify-center bg-white">
                      {getMediaIcon(mediaType, true, 14, 28)}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="start" alignOffset={-10}>
                    <p className="text-[12px] text-white">
                      {capitalizeFirstLetter(mediaType)}
                      {mediaType !== "mixed" && "s"}
                    </p>
                  </TooltipContent>
                </Tooltip>
                <Button
                  variant="sq-outline"
                  size={"sm"}
                  onClick={() => handleGalleryViewChange(true)}
                  disabled={isAdsLoading}
                  className="text-[12px] gap-[6px] bg-white flex items-center py-1 px-2 shadow-sm squircle-radius-[6px] rounded-[6px]"
                >
                  {isAdsLoading ? (
                    <LuLoaderCircle
                      size={14}
                      className="text-brand-primary-400 shrink-0 animate-spin"
                    />
                  ) : (
                    <RiPantoneLine size={14} className="text-brand-primary-400" />
                  )}
                  <span className="overflow-hidden text-brand-primary-600 text-ellipsis text-[12px] font-normal leading-[19.157px] tracking-[0.24px]">
                    {currentAdIndex !== undefined
                      ? `${currentAdIndex + 1} / ${totalAds} ${totalAds === 1 ? "Ad" : "Ads"
                      }`
                      : ""}
                  </span>
                </Button>
                <Button
                  variant="sq-outline"
                  size={"sm"}
                  onClick={onPanelClick}
                  className="hidden group-hover/card:flex h-7 w-7 squircle-radius-[6px] rounded-[6px]"
                >
                  <RiSideBarLine size={14} className="text-brand-primary-400" />
                </Button>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="sq-outline"
                  size={"sm"}
                  onClick={onPrevious}
                  disabled={currentAdIndex === 0 || isAdsLoading}
                  className="w-7 h-7"
                >
                  <RiArrowLeftWideLine className="text-brand-primary-400" size={14} />
                </Button>
                <Button
                  variant="sq-outline"
                  size={"sm"}
                  onClick={onNext}
                  disabled={currentAdIndex === totalAds - 1 || isAdsLoading}
                  className="w-7 h-7"
                >
                  <RiArrowRightWideLine className="text-brand-primary-400" size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
        <CreativeGalleryPreview
          open={isGalleryOpen}
          loading={isGalleryLoading}
          onOpenChange={handleGalleryViewChange}
          creatives={mediaItems}
          groupType={groupType}
          labelConfigId={labelConfigId}
          accountId={accountId}
          creativeType={creativeType}
          labelView={labelView}
          currentGroup={currentGroup}
          enableSearch={mediaItems.length > 4}
          ad_columns={ad_columns}
          totalCreatives={galleryTotalCreatives}
          onLoadMore={onGalleryLoadMore}
        />
      </div>
    </TooltipProvider>
  );
};

export default CardHeader;
